import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { useImmer } from "use-immer";

import { IgLoading, IgTypography } from "@intelligems/ig-theme";
import { Grid } from "@mui/material";

import { useConfigCompare } from "../../api/plugin-configs/get-config-compare";

import { Event } from "./Event";
import { FilterReduceState } from "./index";
import { componentSort, filterByExperience } from "./utils";

interface EventPanelProps {
  filters: FilterReduceState;
  compareEvents: {
    from: { key: string; ts: number };
    to: { key: string; ts: number };
  };
}

export const EventPanel = ({ compareEvents, filters }: EventPanelProps) => {
  const [filteredDiffs, setFilteredDiffs] = useImmer<any>({});

  const configCompare = useConfigCompare(
    {
      from: compareEvents.from?.key,
      to: compareEvents.to?.key,
    },
    {
      enabled: !!compareEvents.to?.key,
    }
  );

  useEffect(() => {
    if (configCompare.isSuccess) {
      setFilteredDiffs(filterByExperience(filters.value, configCompare.data));
    }
  }, [configCompare.isSuccess, configCompare.data?.diffs, filters.value]);

  if (configCompare.isError) {
    return (
      <Grid container item justifyContent={"center"} alignItems={"center"}>
        <IgTypography>This comparison is too large.</IgTypography>
      </Grid>
    );
  }
  if (!configCompare.isSuccess) {
    return (
      <Grid container item justifyContent={"center"} alignItems={"center"}>
        <IgLoading />
      </Grid>
    );
  }

  return (
    <Grid
      container
      item
      alignItems={"center"}
      direction={"column"}
      rowSpacing={1}
    >
      <Grid item>
        <IgTypography variant={"h2"} sx={{ fontWeight: 600 }}>
          {DateTime.fromSeconds(compareEvents.to.ts).toLocaleString(
            DateTime.DATETIME_MED_WITH_SECONDS
          )}
        </IgTypography>
      </Grid>
      {configCompare.data?.diffs?.builtBy?.builtBy?.data?.to ? (
        <Grid item>
          <IgTypography>
            Built by {configCompare.data?.diffs.builtBy.builtBy.data.to}
          </IgTypography>
        </Grid>
      ) : null}
      {configCompare.data?.diffs?.builtBy?.updateReason?.data?.to ? (
        <Grid item>
          <IgTypography>
            {configCompare.data?.diffs.builtBy.updateReason.data.to}
          </IgTypography>
        </Grid>
      ) : null}
      <Grid container item rowSpacing={1} className={"event-panel"}>
        {Object.entries(filteredDiffs)
          .map(([k, value], index) => {
            return Event({ k, value, key: `${k}-${index}` });
          })
          .sort(componentSort)
          .map((i, index) => (
            <Grid
              container
              item
              sx={{ width: "100%" }}
              key={`${i.type}-el-${index}`}
            >
              {i.element}
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
};
